import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type { User } from './types/users.ts';
import type {
  CurrentUserContext,
  InviteResponse,
  RegistrationStatusData,
  SetupAccountResponse,
} from './types/auth.ts';
import type { UserActivationData } from './types/users.ts';

/** Authentication and account-onboarding endpoints. */
export class AuthApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  async login(username: string, password: string): Promise<{ access_token: string; token_type: string }> {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    return this.request(`${this.baseUrl}/auth/token`, {
      method: 'POST',
      body: formData,
    });
  }

  async signup(
    email: string,
    password: string,
    role = 'trader',
    firstName?: string,
    lastName?: string,
    isCompany?: boolean,
    companyName?: string,
  ): Promise<User> {
    return this.request<User>(`${this.baseUrl}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: email,
        password,
        role,
        first_name: firstName,
        last_name: lastName,
        is_company: isCompany,
        company_name: companyName,
      }),
    });
  }

  getRegistrationStatus(): Promise<RegistrationStatusData> {
    return this.request<RegistrationStatusData>(`${this.baseUrl}/auth/registration-status`);
  }

  getCurrentUser(signal?: AbortSignal): Promise<User> {
    return this.request<User>(`${this.baseUrl}/auth/me`, {
      timeoutMs: 8000,
      retries: 1,
      signal,
    });
  }

  getCurrentContext(): Promise<APIResponse<CurrentUserContext>> {
    return this.request<APIResponse<CurrentUserContext>>(
      `${this.baseUrl}/auth/me/context`,
      { timeoutMs: 2500 },
    );
  }

  joinWaitlist(
    email: string,
    firstName?: string,
    lastName?: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request(`${this.baseUrl}/auth/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, first_name: firstName, last_name: lastName }),
    });
  }

  sendInvite(userId: number): Promise<InviteResponse> {
    return this.request<InviteResponse>(`${this.baseUrl}/auth/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
  }

  activateUser(userId: number): Promise<APIResponse<UserActivationData>> {
    return this.request<APIResponse<UserActivationData>>(`${this.baseUrl}/auth/users/${userId}/activate`, {
      method: 'POST',
    });
  }

  verifyInvite(token: string): Promise<{ valid: boolean; email?: string; username?: string }> {
    return this.request(`${this.baseUrl}/auth/verify-invite/${token}`);
  }

  setupAccount(token: string, password: string): Promise<SetupAccountResponse> {
    return this.request<SetupAccountResponse>(`${this.baseUrl}/auth/setup-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
  }
}
